import { listTokenShop } from "@/services/api/tokenShop";
import { compactTokenKey } from "@/utils/tokenPatterns";

let fetchedNames: string[] | null = null;
const rememberedNames: string[] = [];

export function isIgnoredToken(token: string, names: string[]): boolean {
  const raw = (token || "").trim();
  if (!raw || names.length === 0) return false;
  const lower = raw.toLowerCase();
  const compact = compactTokenKey(raw);
  return names.some((name) => {
    const stored = name.trim();
    if (!stored) return false;
    if (stored.toLowerCase() === lower) return true;
    return Boolean(compact) && compactTokenKey(stored) === compact;
  });
}

function mergedIgnoredNames(): string[] {
  const names = [...(fetchedNames || [])];
  for (const token of rememberedNames) {
    if (!isIgnoredToken(token, names)) names.push(token);
  }
  return names;
}

export function knownIgnoredTokenNames(): string[] {
  return mergedIgnoredNames();
}

export function rememberIgnoredToken(name: string): void {
  const token = name.trim();
  if (!token || isIgnoredToken(token, mergedIgnoredNames())) return;
  rememberedNames.push(token);
}

export function rememberIgnoredTokens(names: string[]): void {
  for (const name of names) rememberIgnoredToken(name);
}

export function resetIgnoredTokenMemory(): void {
  fetchedNames = null;
  rememberedNames.length = 0;
}

type IgnoreStamp = {
  field?: string;
  confirmed?: boolean;
  is_split?: boolean;
  auto_split?: boolean;
  split_into?: string[];
};

export function overlayIgnoredNames<T extends IgnoreStamp>(
  status: Record<string, T>,
  names: string[]
): Record<string, T> {
  const next = { ...status };
  const list = names.map((name) => name.trim()).filter(Boolean);
  const stamp = (key: string) => {
    const prior = next[key] || ({} as T);
    next[key] = {
      ...prior,
      field: "ignore",
      confirmed: true,
      is_split: Boolean(
        prior.is_split || (prior.split_into && prior.split_into.length >= 2)
      ),
      auto_split: Boolean(prior.auto_split),
      split_into: prior.split_into,
    };
  };
  for (const token of list) {
    const key =
      Object.keys(next).find(
        (item) => item.toLowerCase() === token.toLowerCase()
      ) || token;
    stamp(key);
  }
  for (const key of Object.keys(next)) {
    if (next[key]?.field === "ignore") continue;
    if (isIgnoredToken(key, list)) stamp(key);
  }
  return next;
}

export function forgetIgnoredTokens(names: string[]): void {
  const drop = names.map((name) => name.trim()).filter(Boolean);
  if (!drop.length) return;
  for (let i = rememberedNames.length - 1; i >= 0; i--) {
    const name = rememberedNames[i];
    if (name && drop.some((token) => isIgnoredToken(name, [token])))
      rememberedNames.splice(i, 1);
  }
  if (fetchedNames) {
    fetchedNames = fetchedNames.filter(
      (name) => !drop.some((token) => isIgnoredToken(name, [token]))
    );
  }
}

export function invalidateIgnoredTokenNames(): void {
  fetchedNames = null;
}

export async function loadIgnoredTokenNames(options?: {
  refresh?: boolean;
}): Promise<string[]> {
  if (fetchedNames && !options?.refresh) return mergedIgnoredNames();
  try {
    const tokens = await listTokenShop("ignore");
    fetchedNames = Object.entries(tokens)
      .filter(([, entry]) => entry?.field === "ignore")
      .map(([name]) => name.trim())
      .filter(Boolean);
  } catch (error) {
    if (!fetchedNames) throw error;
  }
  return mergedIgnoredNames();
}
